import React, { useEffect, useMemo, useState } from "react";
import { Model, settings } from "survey-core";
import { Survey } from "survey-react-ui";
import {
  confirmActionAsyncFunc,
  markdownHandler,
} from "../../components/surveyjs/survey/survey.util";
import { initSurvey } from "../../components/surveyjs/survey/init-survey";
import { getI18n, useTranslation } from "react-i18next";
import { useQuery } from "../../../../utils/hooks/use-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ApplicationDetailTemplate, ApplicationType } from "./template";
import {
  reinstateVerify,
  reinstateVerifyCandidate,
  submitApplicationDetail,
} from "../../network/network";
import { cloneDeep, isEmpty } from "lodash";
import { GlobalHelper } from "../../../../utils/helpers/global-helper";
import moment from "moment";
import { getFullRegionList, getRecruitPublicConfig } from "../../../common/network/network";
import { FRONTEND_ROOT_PATH, ONBOARDING_PATH } from "../../../../routes/constants";
import PageContainer from "../../../../components/page-container/page-container";
import { getTargetCandidate, getToken, parseJwt } from "../../utils";
import { confirm } from "../../../../components/modal/comfirm";
import { commonSlice } from "../../../../redux/common/common-slice";
import "./style.css";
import { RoleEnum } from "../../types";
const { showLoadingAction, hideLoadingAction } = commonSlice.actions;
const dateFormat = "DD/MM/yyyy";
const elements = ApplicationDetailTemplate.pages[0].elements;
export const ApplicationDetailScreen: React.FC = () => {
  initSurvey();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const query: any = useQuery();
  
  // State to track sessionStorage data
  const [sessionStorageData, setSessionStorageData] = useState<any>(null);
  
  // Listen for sessionStorage changes and custom events
  useEffect(() => {
    let hasSetData = false;
    let checkCount = 0;
    
    // Check if current user is a candidate
    const rawToken = getToken();
    const tokenData = rawToken ? parseJwt(rawToken) : {};
    const isCandidate = tokenData.role === RoleEnum.PENDING_CANDIDATE;
    
    const checkSessionStorage = () => {
      if (hasSetData) return null;
      
      checkCount++;
      console.log(`🔍 Checking sessionStorage attempt #${checkCount} (isCandidate: ${isCandidate})`);
      
      try {
        const candidateProfile = sessionStorage.getItem('CANDIDATE_PROFILE');
        
        if (candidateProfile && candidateProfile !== 'null' && candidateProfile !== '0' && candidateProfile !== 'undefined') {
          const parsed = JSON.parse(candidateProfile);
          
          // Check if parsed object has meaningful data (not empty object {})
          if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
            // Also check if applicationDetails exists and is not empty
            if (parsed.applicationDetails && typeof parsed.applicationDetails === 'object' && Object.keys(parsed.applicationDetails).length > 0) {
              console.log('✅ Valid CANDIDATE_PROFILE found:', parsed);
              hasSetData = true;
              setSessionStorageData(parsed);
              return parsed;
            } else {
              console.log('⚠️ CANDIDATE_PROFILE found but applicationDetails is empty:', parsed);
            }
          } else {
            console.log('⚠️ CANDIDATE_PROFILE found but is empty object:', parsed);
          }
        } else {
          console.log('⚠️ CANDIDATE_PROFILE not found or invalid:', candidateProfile);
        }
      } catch (error) {
        console.error('Error parsing sessionStorage:', error);
      }
      return null;
    };
    
    // Initial check
    checkSessionStorage();
    
    // Only do continuous polling for candidates, recruiters don't need this
    if (!hasSetData && isCandidate) {
      const handleSessionStorageReady = () => {
        if (!hasSetData) {
          setTimeout(checkSessionStorage, 50);
        }
      };
      
      window.addEventListener('sessionStorageReady', handleSessionStorageReady);
      window.addEventListener('candidateDataReady', checkSessionStorage);
      
      // Continuous polling until we get valid data
      const pollInterval = setInterval(() => {
        if (!hasSetData) {
          const result = checkSessionStorage();
          if (result) {
            clearInterval(pollInterval);
          }
        } else {
          clearInterval(pollInterval);
        }
      }, 100); // Check every 100ms
      
      // Fallback timeout to stop polling after 30 seconds
      const fallbackTimeout = setTimeout(() => {
        clearInterval(pollInterval);
        if (!hasSetData) {
          // Final attempt
          checkSessionStorage();
        }
      }, 30000);
      
      // Cleanup
      return () => {
        window.removeEventListener('sessionStorageReady', handleSessionStorageReady);
        window.removeEventListener('candidateDataReady', checkSessionStorage);
        clearInterval(pollInterval);
        clearTimeout(fallbackTimeout);
      };
    } else if (!hasSetData && !isCandidate) {
      // For recruiters, just add event listeners without continuous polling
      console.log('👤 Recruiter mode: No continuous polling, only event listeners');
      
      const handleSessionStorageReady = () => {
        if (!hasSetData) {
          setTimeout(checkSessionStorage, 50);
        }
      };
      
      window.addEventListener('sessionStorageReady', handleSessionStorageReady);
      window.addEventListener('candidateDataReady', checkSessionStorage);
      
      // Cleanup for recruiter
      return () => {
        window.removeEventListener('sessionStorageReady', handleSessionStorageReady);
        window.removeEventListener('candidateDataReady', checkSessionStorage);
      };
    }
  }, []);
  
  const applicationDetails = useMemo(() => {
    const _applicationDetails = getTargetCandidate()?.applicationDetails;
    const locationApplicationDetails = location.state?.applicationDetails;
    const sessionStorageApplicationDetails = sessionStorageData?.applicationDetails;
    
    // Merge with priority: sessionStorage > location.state > getTargetCandidate
    return {
      ..._applicationDetails,
      ...locationApplicationDetails,
      ...sessionStorageApplicationDetails,
    };
  }, [location.state, sessionStorageData]);
  
  const [enableApplicationDetailModify, setEnableApplicationDetailModify] = useState(false);
  const [formValues, setForm] = useState({ ...applicationDetails } as any);

  const rawToken = getToken();
  const tokenData = rawToken ? parseJwt(rawToken) : {};

  const [loading, setLoading] = useState(false);

  function generateApplicationDetailData(
    surveyData: any,
    surveyDataPhoneNumber: string | undefined,
    reinstateData: any,
  ): any {
    const isNewRecruit = surveyData.applicationType === ApplicationType.NewRecruit;
    return {
      applicationType: surveyData.applicationType,
      ...(isNewRecruit
        ? {}
        : {
            phone: surveyDataPhoneNumber ?? GlobalHelper.getUserName(),
            formerAgentCode: surveyData.formerAgentCode,
            formerAgentName: reinstateData.agentName || reinstateData.formerAgentName,
            formerAgentLevel: reinstateData.agentLevel || reinstateData.formerAgentLevel,
            agentOffice: surveyData.agentOffice,
            terminationDate: moment(surveyData.terminationDate, "DD/MM/YYYY").format(dateFormat),
            terminationCode: surveyData.terminationCode,
            leaderNumber: reinstateData?.leaderNumber,
            leaderName: surveyData.leaderName,
            leaderLevel: reinstateData.leaderLevel,
            recruiterNumber: reinstateData?.recruiterNumber,
            recruiterName: surveyData.recruiterName,
            recruiterLevel: reinstateData.recruiterLevel,
          }),
    };
  }

  function handleSubmitAndNavigate(
    applicationDetailData: any,
    survey: any,
    query: URLSearchParams,
    location: any,
    navigate: Function,
    setLoading: Function,
  ) {
    submitApplicationDetail(applicationDetailData, survey, async () => {
      setLoading(false);
      
      // Clear sessionStorage after successful submit
      try {
        sessionStorage.removeItem('applicationDetails');
      } catch (error) {
        console.warn('Failed to clear applicationDetails from sessionStorage:', error);
      }
      
      if (query.get("from") === "baseapp") return;

      navigate(
        `${FRONTEND_ROOT_PATH}${
          location.state?.from ?? `/web/${ONBOARDING_PATH}/join-now`
        }?${query.toString()}`,
        {
          state: { ...location.state, applicationDetails: applicationDetailData },
        },
      );
    });
  }

  function resetReinstateFields(sender: any) {
    sender.setValue("recruiterName", "");
    sender.setValue("leaderName", "");
    sender.setValue("agentOffice", "");
    sender.setValue("terminationDate", "");
    sender.setValue("terminationCode", "");
  }

  function setReinstateFields(sender: any, data: any) {
    sender.setValue("recruiterName", data.recruiterName);
    sender.setValue("leaderName", data.leaderName);
    sender.setValue("agentOffice", data.agentOffice);
    sender.setValue(
      "terminationDate",
      data.terminationDate || moment(data.terminatedDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
    );
    sender.setValue("terminationCode", data.terminationCode);
  }

  function showReinstateError(confirm: Function, t: Function, message: string) {
    confirm({
      message,
      confirmButtonText: t("back"),
      disableCancel: true,
    });
  }

  async function handleReinstateVerification({
    tokenData,
    agentCode,
    survey,
    sender,
    setLoading,
    confirm,
    t,
  }: {
    tokenData: any;
    agentCode: string;
    survey: any;
    sender: any;
    setLoading: Function;
    confirm: Function;
    t: Function;
  }) {
    const isCandidate = tokenData.role === RoleEnum.PENDING_CANDIDATE;
    const userName = GlobalHelper.getUserName();

    try {
      setLoading(true);

      if (isCandidate) {
        const paramsCandidate = { phoneNumber: userName };
        const response = await reinstateVerifyCandidate(paramsCandidate, survey);

        const findReinstate = Array.isArray(response)
          ? response.findIndex(
              (item: any) => item.applicationDetails?.applicationType === "ForReinstatement",
            )
          : -1;

        const matchedData = response?.[findReinstate];

        if (matchedData && matchedData.applicationDetails?.formerAgentCode === agentCode) {
          setReinstateFields(sender, matchedData.applicationDetails);
        } else {
          const message =
            matchedData?.applicationDetails?.formerAgentCode !== agentCode
              ? "Kode agen tidak valid"
              : "Pilihan pengajuan aplikasi keagenan Anda tidak sesuai dengan pilihan perekrut";

          showReinstateError(confirm, t, message);
          resetReinstateFields(sender);
        }
      } else {
        const data = { leader: userName, recruiter: agentCode ?? "" };
        const response = await reinstateVerify(data, survey);

        if (response && (response.responseCode === 0 || response.responseCode === 13)) {
          setReinstateFields(sender, response.data);
        } else {
          const message =
            response.responseCode === -255 ? "Unknown error" : response.responseMessage;

          showReinstateError(confirm, t, message);
          resetReinstateFields(sender);
        }
      }
    } catch (error) {
      resetReinstateFields(sender);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (applicationDetails && Object.keys(applicationDetails).length > 0) {
      setForm((prevForm: any) => ({
        ...prevForm,
        ...applicationDetails,
      }));
    }
  }, [applicationDetails]);
  useEffect(() => {
    GlobalHelper.getGlobalDispatch()?.(loading ? showLoadingAction() : hideLoadingAction());
  }, [loading]);
  const [pageInitiating, setPageInitiating] = useState(true);
  useEffect(() => {
    convertTemplate(ApplicationDetailTemplate);
    const promises: any[] = [getRecruitPublicConfig()];
    if (!GlobalHelper.getUserName()) {
      promises.push(getFullRegionList());
    }
    Promise.all(promises)
      .then((reses: any) => {
        if (reses.length === 0) {
          return;
        }
        const [recruitPublicConfigRes, regionConfigs] = reses;
        setEnableApplicationDetailModify(
          recruitPublicConfigRes.Recruitment?.candidateProfile?.enableApplicationDetailModify,
        );
        if (!regionConfigs) {
          return;
        }

        const regionPhoneField: any = elements.find((item: any) => item.name === "regionPhone");
        if (regionPhoneField?.templateElements?.[0] && regionConfigs) {
          const regionOpts = Object.values(regionConfigs).map((region: any) => ({
            text: `${region.code}`,
            key: region.code,
            value: region.code,
            reg: region.regex,
          }));
          // @ts-ignore
          regionPhoneField.templateElements[0].choices = regionOpts;
          setForm((pre: any) => ({
            ...pre,
            regionCode: regionOpts?.[0].key ?? "+852",
          }));
        } else {
          setForm((pre: any) => ({
            ...pre,
            regionCode: "+852",
          }));
        }
      })
      .finally(() => {
        setPageInitiating(false);
      });
  }, []);
  const form = useMemo(() => {
    if (pageInitiating) return null;
    const survey = new Model(cloneDeep(ApplicationDetailTemplate));
    survey.showPageTitles = false;
    survey.showNavigationButtons = "none";
    survey.questionErrorLocation = "bottom";
    survey.showCompletedPage = false;
    const defaultValue = {
      regionPhone: [
        {
          regionCode: formValues.regionCode,
          phoneNumber: formValues.phoneNumber,
        },
      ],
      ...formValues,
    };

    survey.mergeData(defaultValue);

    if (!enableApplicationDetailModify && !isEmpty(applicationDetails)) {
      survey.mode = "display";
      survey.getAllQuestions().forEach((question: any) => {
        question.readOnly = true;
      });
    }

    survey.onComplete.add((_sender: any) => {});
    survey.onTextMarkdown.add(markdownHandler);
    let debounceTimer: string | number | NodeJS.Timeout | undefined;
    survey.onValueChanged.add(function (sender, options) {
      if (options.name === "formerAgentCode") {
        clearTimeout(debounceTimer);
        const agentCode = options.value;
        const regexAgentCode = /^[0-9]{8}$/;
        if (!agentCode || !regexAgentCode.test(agentCode)) {
          return;
        }

        debounceTimer = setTimeout(async () => {
          await handleReinstateVerification({
            tokenData,
            agentCode,
            survey,
            sender,
            setLoading,
            confirm,
            t,
          });
        }, 500);
      }
    });

    survey.onGetQuestionTitleActions.add((_, options) => {
      if (options.question.tooltips) {
        options.titleActions = [
          {
            id: "show-popover",
            component: "popover",
            tooltip: options.question.tooltips,
          },
        ];
      }
    });
    settings.confirmActionAsync = confirmActionAsyncFunc;

    return (
      <div className="flex flex-1 flex-col max-w-md sm:max-w-xl lg:max-w-xl px-6">
        <div className="flex flex-col w-full pb-4">
          <div className=" bg-white w-full rounded-tr-3xl h-10"></div>
          <Survey
            currentPageNo={0}
            className="survey-common"
            id="application-detail"
            model={survey}
          />

          <div className=" w-full bg-white pt-2 pb-7 flex items-center justify-between gap-4">
            <button
              className="bg-red-500 flex-1 text-white font-semibold py-3  rounded-lg shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75"
              onClick={async () => {
                let isValid = false;
                isValid = survey.validateCurrentPage();
                if (!isValid) {
                  return;
                }
                setLoading(true);
                const surveyData = survey.data;
                const surveyDataRegionPhone = surveyData.regionPhone[0];
                const surveyDataPhoneNumber =
                  surveyDataRegionPhone.regionCode && surveyDataRegionPhone.phoneNumber
                    ? `${surveyDataRegionPhone.regionCode} ${surveyDataRegionPhone.phoneNumber}`
                    : undefined;
                let message = "";
                let disableConfirmButton = true;
                if (tokenData.role === RoleEnum.PENDING_CANDIDATE) {
                  // CANDIDATE SIDE
                  if (surveyData.applicationType === ApplicationType.NewRecruit) {
                    const paramsCandidate = { phoneNumber: GlobalHelper.getUserName() };
                    const getReinstateVerify = await reinstateVerifyCandidate(
                      paramsCandidate,
                      survey,
                    );
                    if (Array.isArray(getReinstateVerify)) {
                      const findReinstate = getReinstateVerify.findIndex(
                        (item: any) =>
                          item.applicationDetails?.applicationType === "ForReinstatement",
                      );
                      if (findReinstate !== -1) {
                        const matchedData = getReinstateVerify[findReinstate];
                        message =
                          "Pilihan pengajuan aplikasi keagenan Anda tidak sesuai dengan pilihan perekrut";
                        disableConfirmButton = false;
                        setLoading(false);
                        confirm({
                          message: message,
                          confirmButtonText: disableConfirmButton
                            ? t("back")
                            : "Lanjutkan Pengajuan Kode Agen Baru",
                          cancelButtonText: t("back"),
                          onConfirm: () => {
                            if (!disableConfirmButton) {
                              const applicationDetailData = generateApplicationDetailData(
                                surveyData,
                                surveyDataPhoneNumber,
                                matchedData?.applicationDetails,
                              );
                              handleSubmitAndNavigate(
                                applicationDetailData,
                                survey,
                                query,
                                location,
                                navigate,
                                setLoading,
                              );
                            }
                          },
                          disableCancel: disableConfirmButton,
                        });
                      } else {
                        const applicationDetailData = generateApplicationDetailData(
                          surveyData,
                          surveyDataPhoneNumber,
                          null,
                        );
                        handleSubmitAndNavigate(
                          applicationDetailData,
                          survey,
                          query,
                          location,
                          navigate,
                          setLoading,
                        );
                      }
                    } else {
                      const applicationDetailData = generateApplicationDetailData(
                        surveyData,
                        surveyDataPhoneNumber,
                        null,
                      );
                      handleSubmitAndNavigate(
                        applicationDetailData,
                        survey,
                        query,
                        location,
                        navigate,
                        setLoading,
                      );
                    }
                  } else {
                    const paramsCandidate = { phoneNumber: GlobalHelper.getUserName() };
                    const getReinstateVerify = await reinstateVerifyCandidate(
                      paramsCandidate,
                      survey,
                    );

                    if (Array.isArray(getReinstateVerify)) {
                      const findReinstate = getReinstateVerify.findIndex(
                        (item: any) =>
                          item.applicationDetails?.applicationType === "ForReinstatement",
                      );

                      const matchedData = getReinstateVerify[findReinstate];
                      if (
                        findReinstate !== -1 &&
                        matchedData?.applicationDetails?.formerAgentCode ===
                          surveyData.formerAgentCode
                      ) {
                        const applicationDetailData = generateApplicationDetailData(
                          surveyData,
                          surveyDataPhoneNumber,
                          matchedData?.applicationDetails,
                        );
                        handleSubmitAndNavigate(
                          applicationDetailData,
                          survey,
                          query,
                          location,
                          navigate,
                          setLoading,
                        );
                      } else {
                        message =
                          "Pilihan pengajuan aplikasi keagenan Anda tidak sesuai dengan pilihan perekrut";
                        setLoading(false);
                        confirm({
                          message: message,
                          confirmButtonText: t("back"),
                          onConfirm: () => {},
                          disableCancel: true,
                        });
                      }
                    }
                  }
                } else {
                  // RECRUITER SIDE
                  if (surveyData.applicationType === ApplicationType.NewRecruit) {
                    const applicationDetailData = generateApplicationDetailData(
                      surveyData,
                      surveyDataPhoneNumber,
                      null,
                    );
                    handleSubmitAndNavigate(
                      applicationDetailData,
                      survey,
                      query,
                      location,
                      navigate,
                      setLoading,
                    );
                  } else {
                    const data = {
                      leader: GlobalHelper.getUserName(),
                      recruiter: surveyData.formerAgentCode ?? "",
                    };
                    const getReinstateVerify = await reinstateVerify(data, survey);

                    if (
                      getReinstateVerify &&
                      (getReinstateVerify.responseCode === 0 ||
                        getReinstateVerify.responseCode === 13)
                    ) {
                      const applicationDetailData = generateApplicationDetailData(
                        surveyData,
                        surveyDataPhoneNumber,
                        getReinstateVerify?.data,
                      );
                      handleSubmitAndNavigate(
                        applicationDetailData,
                        survey,
                        query,
                        location,
                        navigate,
                        setLoading,
                  );
                    } else {
                      setLoading(false);
                      message = getReinstateVerify.responseMessage;
                      disableConfirmButton = true;
                      confirm({
                        message: message,
                        confirmButtonText: t("back"),
                        onConfirm: () => {},
                        disableCancel: true,
                });
                    }
                  }
                }
              }}>
              {t("proceed")}
            </button>
          </div>
        </div>
      </div>
    );
  }, [formValues, pageInitiating, enableApplicationDetailModify, applicationDetails]);
  return <PageContainer title={t("Common.application_detail_screen_title")}>{form}</PageContainer>;
};

const convertTemplate = (template: any) => {
  // Function to translate a given field if it exists
  const translateField = (obj: any, field: string) => {
    if (obj?.[field]) {
      obj[field] = getI18n().t(obj[field]);
    }
  };

  // const terminationDateField = template.pages[0].elements.find(
  //   (item: any) => item.name === "terminationDate",
  // );
  // const appointmentDateField = template.pages[0].elements.find(
  //   (item: any) => item.name === "appointmentDate",
  // );
  // const maxDate = moment().format("YYYY-MM-DD");
  // terminationDateField.max = maxDate;
  // appointmentDateField.max = maxDate;

  // Loop through each element in the first page
  template.pages[0].elements = template.pages[0].elements.map((q: any) => {
    // Translate title, labelTrue, labelFalse, requiredErrorText fields
    translateField(q, "title");
    translateField(q, "placeholder");
    translateField(q, "labelTrue");
    translateField(q, "labelFalse");
    translateField(q, "requiredErrorText");
    translateField(q, "tooltips");
    translateField(q?.modalConfig, "modalTitle");

    // translate choices if they exist
    if (q.choices && Array.isArray(q.choices)) {
      q.choices.forEach((choice: any) => {
        translateField(choice, "text");
      });
    }

    // Check if the element has validators and translate their text fields
    if (q.validators && Array.isArray(q.validators)) {
      q.validators.forEach((validator: any) => {
        translateField(validator, "text");
      });
    }

    // If the element is a paneldynamic type, we need to handle nested templateElements
    if (q.type === "paneldynamic" && q.templateElements && Array.isArray(q.templateElements)) {
      q.templateElements = q.templateElements.map((te: any) => {
        translateField(te, "title");
        translateField(te, "labelTrue");
        translateField(te, "labelFalse");
        translateField(te, "requiredErrorText");
        translateField(te, "placeholder");
        translateField(te?.modalConfig, "modalTitle");

        if (te.validators && Array.isArray(te.validators)) {
          te.validators.forEach((validator: any) => {
            translateField(validator, "text");
          });
        }

        return te;
      });
    }

    return q;
  });
};
